import {
  Component,
  Inject,
  OnInit,
  ViewChild,
  ElementRef,
  AfterViewInit,
  Optional,
  TemplateRef,
} from '@angular/core';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialog,
} from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { UploadDocumentDialogComponent } from '../../../../components/upload-document-dialog/upload-document-dialog.component';
import {
  Firestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
} from '@angular/fire/firestore';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
} from '@angular/fire/storage';
import { UxserviceService } from 'src/app/shared/services/uxservice.service';
import { Auth } from '@angular/fire/auth';
import { BookingLockService } from 'src/app/shared/services/booking-lock.service';
import { NotificationService } from 'src/app/shared/services/notification.service';

import { RealtimeBookingService } from 'src/app/shared/services/realtime-booking.service';
import { Subscription } from 'rxjs';

export interface TimesheetEntryData {
  bookingId: string;
  booking: any;
}

export interface TimesheetRow {
  date: string;
  startTime: string;
  endTime: string;
  breakHours: number;
  totalHours: number;
  description?: string;
  addedBy?: 'Vendor' | 'Customer';
  isDisputed?: boolean;
  disputeReason?: string;
  status?: 'OK' | 'Disputed' | 'Changed' | 'Pending' | 'Resolved';
  correctionStartTime?: string;
  correctionEndTime?: string;
  correctionBreakHours?: number;
  correctionTotalHours?: number;
  // Field-level Dispute Tracking
  isStartTimeDisputed?: boolean;
  isEndTimeDisputed?: boolean;
  isBreakDisputed?: boolean;
  // Audit Trail
  history?: TimesheetHistoryEntry[];
  // UI State
  isExpanded?: boolean;
  rowDate?: string; // Added to map back in unified history
}

export interface TimesheetHistoryEntry {
  timestamp: string;
  user: string;
  role: 'Vendor' | 'Customer';
  action: string;
  details?: string;
  rowDate?: string; // Reference to which row date this log belongs to
}

@Component({
  selector: 'app-timesheet-entry-dialog',
  templateUrl: './timesheet-entry-dialog.component.html',
  styleUrls: ['./timesheet-entry-dialog.component.scss'],
})
export class TimesheetEntryDialogComponent implements OnInit, AfterViewInit {
  @ViewChild('sigCanvas') sigCanvas!: ElementRef<HTMLCanvasElement>;
  private ctx!: CanvasRenderingContext2D;
  private isDrawing = false;
  public errorState: boolean = false;
  today = new Date();
  selectedTabIndex = 0;

  isLoading = true;
  rows: TimesheetRow[] = [];
  showHistory: boolean = false;
  isHistoryCollapsed: boolean = true;

  isVendor: boolean = false;
  isCustomer: boolean = false;
  isCompleted: boolean = false;

  // Timesheet lifecycle status: draft → submitted → rejected → completed
  timesheetStatus: 'draft' | 'submitted' | 'rejected' | 'completed' = 'draft';
  customerRejectionComment: string = '';
  showRejectionModal: boolean = false;
  tempRejectionComment: string = '';
  customerSignatureUrl?: string;
  customerSignatureBase64?: string; // Added
  vendorSignatureUrl?: string;
  vendorSignatureBase64?: string; // Added

  // Agreement tracking — both parties must agree before signing is allowed
  customerAgreed: boolean = false;
  vendorAgreed: boolean = false;
  vendorName: string = '';
  customerName: string = '';
  vendorDetails: any = null;
  customerDetails: any = null;
  tsNumber: string = '';
  uploadedTimesheetUrl: string = '';
  rowsInitiatedBy?: 'Vendor' | 'Customer';
  currentuserImage: string | null = null;
  currentusername: string | null = null;
  showInitialChoice: boolean = false;
  showBatchSetup: boolean = false;
  setupStep: number = 1;
  showUploadSection: boolean = false;
  selectedFile: File | null = null;
  isSaving: boolean = false;
  excludedDates: string[] = [];
  tempOffDate: string = '';
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  private rtSub?: Subscription;

  // Batch Auto-fill inputs
  batchStartDate: string = new Date().toISOString().split('T')[0];
  batchEndDate: string = new Date().toISOString().split('T')[0];
  batchStartTime: string = '09:00';
  batchEndTime: string = '17:00';
  batchBreakHours: number = 0;
  minDate: string = '';
  maxDate: string = '';

  // Simplified UI State
  showAdvanced: boolean = false;
  vendorVerificationHours: number | null = null;
  customerVerificationHours: number | null = null;
  standardStartTime: string = '09:00';
  standardEndTime: string = '17:00';
  standardBreakHours: number = 1;

  weekdaysSelection: boolean[] = [false, true, true, true, true, true, false];
  showWizardCalendar: boolean = false;
  calendarMode: 'off-day' | 'batch-start' | 'batch-end' | 'row-date' =
    'row-date';

  timeOptions = this.generateTimeOptions();

  // --- CUSTOM CALENDAR STATE ---
  // --- CUSTOM CALENDAR STATE ---
  activeCalendarRow: TimesheetRow | null = null;
  currentMonth: number = new Date().getMonth();
  currentYear: number = new Date().getFullYear();
  weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  calendarDays: any[] = [];


  generateTimeOptions() {
    // Keep for potential future use or delete, but formatTo12Hr is what we need now
    const options = [];
    for (let h = 0; h < 24; h++) {
      for (let m of ['00', '15', '30', '45']) {
        const hh = h.toString().padStart(2, '0');
        const val = `${hh}:${m}`;
        const ampm = h >= 12 ? 'PM' : 'AM';
        const displayH = h % 12 === 0 ? 12 : h % 12;
        const displayHH = displayH.toString().padStart(2, '0');
        options.push({ value: val, label: `${displayHH}:${m} ${ampm}` });
      }
    }
    return options;
  }

  formatTo12Hr(time24: string): string {
    if (!time24) return '';
    const parts = time24.split(':');
    if (parts.length !== 2) return time24;
    let hours = parseInt(parts[0], 10);
    const m = parts[1];
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 becomes 12
    const hoursStr = hours.toString().padStart(2, '0');
    return `${hoursStr}:${m} ${ampm}`;
  }

  get vendorRows(): TimesheetRow[] {
    // Legacy support or internal use: returns all rows as the 'Master' list
    return this.rows;
  }

  get customerRows(): TimesheetRow[] {
    // No longer used in split mode but kept for template compatibility if any
    return this.rows;
  }

  get hasDisputedRows(): boolean {
    return this.rows.some((r) => r.isDisputed);
  }

  constructor(
    @Optional() public dialogRef: MatDialogRef<TimesheetEntryDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: TimesheetEntryData | null,
    private afs: Firestore,
    private auth: Auth,
    private ux: UxserviceService,
    private bookingLock: BookingLockService,
    private notificationService: NotificationService,
    private dialog: MatDialog,
    private rtBooking: RealtimeBookingService,
    private route: ActivatedRoute,
  ) {}

  onClose(result?: any) {
    if (this.dialogRef) {
      this.dialogRef.close(result);
    } else {
      // In page mode (new tab), maybe we just go back or close the tab
      this.ux.instantCloseOverlay();
      if (window.opener) {
        window.close();
      } else {
        // Fallback for regular navigation
        window.history.back();
      }
    }
  }

  get isMyDraft(): boolean {
    const myRole = this.isVendor ? 'Vendor' : 'Customer';
    return (
      this.rows.length > 0 &&
      !this.rowsInitiatedBy &&
      this.rows.some((r) => (r.addedBy || 'Vendor') === myRole)
    );
  }

  get shouldShowTable(): boolean {
    // Show table if it's already been formally initiated/sent
    if (this.rowsInitiatedBy) return true;
    // OR if I am currently working on a draft
    if (this.isMyDraft) return true;
    // OR if it's the completed/signed view
    if (this.isSignedView) return true;
    // OR if we finished/skipped setup
    if (
      !this.showInitialChoice &&
      !this.showBatchSetup &&
      !this.showUploadSection
    )
      return true;
    return false;
  }

  get shouldShowGeneration(): boolean {
    // Show generation panel if I am allowed to edit
    // AND nobody has formally initiated yet
    // AND I don't have my own draft rows currently
    // AND there's no uploaded document overriding the table
    return (
      this.canEdit &&
      !this.rowsInitiatedBy &&
      !this.isMyDraft &&
      !this.uploadedTimesheetUrl &&
      this.showBatchSetup // Only show generation panel if we are in setup mode
    );
  }

  async ngOnInit(): Promise<void> {
    let data = this.data;

    if (!data || !data.bookingId) {
      const bId = this.route.snapshot.paramMap.get('id');
      if (bId) {
        this.data = { bookingId: bId, booking: null };
        data = this.data; // Sync local variable
      }
    }

    if (!data || !data.bookingId) {
      console.error('❌ Timesheet Dialog: Missing bookingId!', data);
      this.isLoading = false;
      this.errorState = true;
      return;
    }

    this.tsNumber =
      'TS-' + (data.bookingId?.substring(0, 6).toUpperCase() || 'XXXXXX');
    this.uploadedTimesheetUrl = data.booking?.uploadedTimesheetUrl || '';

    // If used as a page, we might need to fetch the full booking object if not provided
    await this.initializeData();
    this.initRealtimeSync();

    // Determine if we should show initial choice (Upload vs Generate)
    // Similar to LPO: if not completed, no rows initiated, and no uploaded timesheet
    if (
      !this.isCompleted &&
      !this.rowsInitiatedBy &&
      this.rows.length <= 1 &&
      !this.uploadedTimesheetUrl
    ) {
      // Check if the single row is empty
      const isFirstRowEmpty =
        this.rows.length === 0 ||
        (!this.rows[0].startTime && !this.rows[0].endTime);
      if (isFirstRowEmpty) {
        this.showInitialChoice = true;
      }
    }
  }

  initRealtimeSync() {
    const data = this.data;
    if (!data) return;

    this.rtSub = this.rtBooking
      .watchBooking(data.bookingId)
      .subscribe((rtData) => {
        if (!rtData) return;

        if (rtData.status && data.booking) {
          data.booking.status = rtData.status;
        }

        if (rtData.timesheet) {
          const ts = rtData.timesheet;

          if (ts.isCompleted !== undefined) this.isCompleted = ts.isCompleted;
          if (ts.customerSignatureUrl)
            this.customerSignatureUrl = ts.customerSignatureUrl;
          if (ts.customerSignatureBase64)
            this.customerSignatureBase64 = ts.customerSignatureBase64;
          if (ts.vendorSignatureUrl)
            this.vendorSignatureUrl = ts.vendorSignatureUrl;
          if (ts.vendorSignatureBase64)
            this.vendorSignatureBase64 = ts.vendorSignatureBase64;
          if (ts.vendorVerificationHours !== undefined)
            this.vendorVerificationHours = ts.vendorVerificationHours;
          if (ts.customerVerificationHours !== undefined)
            this.customerVerificationHours = ts.customerVerificationHours;
          if (ts.timesheetStatus) this.timesheetStatus = ts.timesheetStatus;
          if (ts.customerRejectionComment !== undefined)
            this.customerRejectionComment = ts.customerRejectionComment;
          // Sync agreement status from realtime DB
          if (ts.customerAgreed !== undefined) this.customerAgreed = ts.customerAgreed;
          if (ts.vendorAgreed !== undefined) this.vendorAgreed = ts.vendorAgreed;

          if (ts.rows) {
            this.rows = ts.rows;
          }
          if (ts.rowsInitiatedBy) {
            this.rowsInitiatedBy = ts.rowsInitiatedBy;
          }
        }
      });
  }

  ngOnDestroy() {
    this.rtSub?.unsubscribe();
  }

  initBatchDates() {
    const data = this.data;
    if (!data || !data.booking) return;
    const b = data.booking;

    const start = b.customer?.startDate || b.startDate;
    const end = b.customer?.endDate || b.endDate;

    if (start) {
      const d = this.getBookingDate(start);
      if (d) {
        const iso = this.toLocalISOString(d);
        this.batchStartDate = iso;
        this.minDate = iso;
      }
    }
    if (end) {
      const d = this.getBookingDate(end);
      if (d) {
        const iso = this.toLocalISOString(d);
        this.batchEndDate = iso;
        this.maxDate = iso;
      }
    }

    // Automatically select work days based on booking period
    if (start && end) {
      const sDate = this.getBookingDate(start);
      const eDate = this.getBookingDate(end);
      if (sDate && eDate) {
        const daysPresent = new Set<number>();
        let current = new Date(sDate);
        let count = 0;
        // Check which days of the week are covered by the booking
        while (current <= eDate && count < 7) {
          daysPresent.add(current.getDay());
          current.setDate(current.getDate() + 1);
          count++;
        }

        // If the booking is less than a full week, only select the days present
        // Otherwise, stick to the standard Mon-Fri default
        const diffDays =
          Math.ceil(
            (eDate.getTime() - sDate.getTime()) / (1000 * 60 * 60 * 24),
          ) + 1;
        if (diffDays < 7) {
          this.weekdaysSelection = [0, 1, 2, 3, 4, 5, 6].map((d) =>
            daysPresent.has(d),
          );
        } else {
          // Default: Mon-Fri
          this.weekdaysSelection = [false, true, true, true, true, true, false];
        }
      }
    }
  }

  private toLocalISOString(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  // Helper date parser
  private getBookingDate(field: any): Date | null {
    if (!field) return null;
    if (typeof field.toDate === 'function') return field.toDate();
    if (field instanceof Date) return field;
    return new Date(field);
  }

  async initializeData() {
    const data = this.data;
    if (!data) return;

    this.isLoading = true;
    try {
      if (!data.booking && data.bookingId) {
        const docRef = doc(this.afs, `booking-list/${data.bookingId}`);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          data.booking = { id: snap.id, ...snap.data() };
          this.uploadedTimesheetUrl = data.booking.uploadedTimesheetUrl || '';
        } else {
          console.error('❌ Booking not found for ID:', data.bookingId);
          this.errorState = true;
          return;
        }
      }

      if (data.booking) {
        if (data.booking.uploadedTimesheetUrl) {
          this.uploadedTimesheetUrl = data.booking.uploadedTimesheetUrl;
        }

        // Run independent database fetches in parallel to optimize load speed
        const promises: Promise<any>[] = [
          this.loadExistingTimesheet(),
          this.fetchParticipantNames()
        ];

        // Fetch equipment details if not present
        if (!data.booking.equipment && data.booking.equipmentId) {
          const eqRef = doc(this.afs, 'equipment-list', data.booking.equipmentId);
          promises.push(
            getDoc(eqRef).then((eqSnap) => {
              if (eqSnap.exists() && data.booking) {
                data.booking.equipment = { id: eqSnap.id, ...eqSnap.data() };
              }
            })
          );
        }

        await Promise.all(promises);
        this.initBatchDates();
      }
    } catch (error) {
      console.error('Error initializing data:', error);
    } finally {
      this.isLoading = false;
    }
  }

  async fetchParticipantNames() {
    const data = this.data;
    if (!data || !data.booking) return;

    // Fetch Vendor Details
    const vendorId = data.booking?.equipment?.bywhom || data.booking?.vendorId;
    if (vendorId) {
      this.isVendor = this.auth.currentUser?.uid === vendorId;
      const vendorRef = doc(this.afs, `users-list/${vendorId}`);
      const snap = await getDoc(vendorRef);
      if (snap.exists()) {
        const d = snap.data();
        const accountDetails = d['accountdetails'] || {};

        this.vendorName =
          d['companyName'] ||
          d['displayName'] ||
          accountDetails['companyname'] ||
          'Vendor';

        this.vendorDetails = {
          companyName: this.vendorName,
          address:
            accountDetails['companyAddress'] ||
            accountDetails['address'] ||
            d['companyAddress'] ||
            d['address'] ||
            '',
          phone:
            accountDetails['phoneNumber'] ||
            d['phoneNumber'] ||
            d['phone'] ||
            '',
          telephone: d['telephone'] || '',
          email: accountDetails['emailid'] || d['email'] || d['emailid'] || '',
          logoUrl:
            d['companyLogoUrl'] ||
            accountDetails['logoUrl'] ||
            d['photoURL'] ||
            null,
          logoBase64: d['companyLogoBase64'] || null, // Added
        };
      }
    }

    // Fetch Customer Details
    const customerId = this.data!.booking.bywhom;
    if (customerId) {
      this.isCustomer = this.auth.currentUser?.uid === customerId;
      const custRef = doc(this.afs, `users-list/${customerId}`);
      const snap = await getDoc(custRef);
      if (snap.exists()) {
        const d = snap.data();
        this.customerName = d['companyName'] || d['displayName'] || 'Customer';

        this.customerDetails = {
          companyName: this.customerName,
          address: d['companyAddress'] || d['address'] || d['location'] || '',
          phone: d['phoneNumber'] || d['phone'] || '',
          email: d['email'] || '',
          logoUrl: d['companyLogoUrl'] || d['photoURL'] || null,
          logoBase64: d['companyLogoBase64'] || null, // Added
        };
      }
    } else {
      this.customerDetails = {
        companyName: this.customerName,
        address: this.data!.booking.customer?.location || '',
        phone: this.data!.booking.customer?.phone || '',
        email: '',
        logoUrl: null,
      };
    }

    // Fetch Logged-in User Profile (for navbar)
    const currentUid = this.auth.currentUser?.uid;
    if (currentUid) {
      const userRef = doc(this.afs, `users-list/${currentUid}`);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const d = userSnap.data();
        this.currentusername = d['displayName'] || d['companyName'] || 'User';
        this.currentuserImage = d['photoURL'] || d['companyLogoUrl'] || null;
      }
    }
  }

  ngAfterViewInit() {
    // Canvas init handled on tab change or draw start
  }

  async loadExistingTimesheet() {
    try {
      if (!this.data?.bookingId) return;

      const docRef = doc(
        this.afs,
        `booking-list/${this.data!.bookingId}/timesheets/main`,
      );
      const snap = await getDoc(docRef);

      if (snap.exists()) {
        const d = snap.data();
        this.customerSignatureUrl = d['customerSignatureUrl'];
        this.customerSignatureBase64 = d['customerSignatureBase64']; // Added
        this.vendorSignatureUrl = d['vendorSignatureUrl'];
        this.vendorSignatureBase64 = d['vendorSignatureBase64']; // Added
        this.vendorVerificationHours = d['vendorVerificationHours'] || null;
        this.customerVerificationHours = d['customerVerificationHours'] || null;
        this.isCompleted = d['isCompleted'] || false;
        this.rowsInitiatedBy = d['rowsInitiatedBy'] || null;
        this.timesheetStatus = d['timesheetStatus'] || (this.isCompleted ? 'completed' : (this.rowsInitiatedBy ? 'submitted' : 'draft'));
        this.customerRejectionComment = d['customerRejectionComment'] || '';
        // Load agreement status
        this.customerAgreed = d['customerAgreed'] || false;
        this.vendorAgreed = d['vendorAgreed'] || false;

        // Fallback for legacy data: if rows exist but no initiator, assume Vendor
        if (d['rows'] && d['rows'].length > 0 && !this.rowsInitiatedBy) {
          this.rowsInitiatedBy = 'Vendor';
        }

        // Load rows if they exist, carefully converting Timestamps back to Dates
        if (d['rows']) {
          this.rows = d['rows'].map((r: any) => {
            let dateStr = '';
            if (r.date) {
              if (typeof r.date === 'string') {
                dateStr = r.date;
              } else {
                const dObj = r.date.toDate ? r.date.toDate() : new Date(r.date);
                dateStr = this.formatDisplayDate(dObj);
              }
            }
            return {
              ...r,
              date: dateStr,
              breakHours:
                r.breakHours ?? (r.breakMinutes ? r.breakMinutes / 60 : 0), // Migrate minutes to hours
              addedBy: r.addedBy || 'Vendor', // Default legacy rows to Vendor
              isExpanded: r.isDisputed || r.isExpanded || false,
            };
          });
        }
      } else {
        // Initialize rows based on booking dates?
        // For now, start with one empty row
        if (!this.isCompleted) this.addRow();
      }
    } catch (error) {
      console.error('Error loading timesheet:', error);
      this.ux.showError('Failed to load existing timesheet.');
    }
  }

  get canEdit(): boolean {
    const data = this.data;
    if (this.isCompleted || !data || !data.booking) return false;

    const b = data.booking;
    const rawPriceType = b?.priceType || b?.customer?.priceType || '';
    const priceType = rawPriceType.toLowerCase().replace('_', ' ');

    const isDailyOrTrip = priceType === 'daily' || priceType === 'per trip';
    const isMonthlyOrWeekly = priceType === 'monthly' || priceType === 'weekly';

    if (isDailyOrTrip && this.isVendor) return true;
    if (isMonthlyOrWeekly && this.isCustomer) return true;

    // Relaxed rule: If user is Vendor or Customer, they can "edit" in general (add rows),
    // but specific row editing is controlled by canEditRow
    if (this.isVendor || this.isCustomer) return true;

    return false;
  }

  canEditRow(row: TimesheetRow): boolean {
    if (this.isCompleted) return false;

    const myRole = this.isVendor ? 'Vendor' : 'Customer';

    // Case 1: If not initiated yet, current user can edit their draft
    if (!this.rowsInitiatedBy) return true;

    // Case 2: Only the initiator can edit — but NOT once the timesheet is submitted/rejected/completed
    if (this.rowsInitiatedBy === myRole) {
      // Lock rows once the timesheet has been formally submitted — no more editing
      if (this.timesheetStatus === 'submitted' || this.timesheetStatus === 'rejected' || this.timesheetStatus === 'completed') {
        return false;
      }
      return true;
    }

    return false;
  }

  canEditDispute(row: TimesheetRow): boolean {
    if (this.isCompleted) return false;

    const myRole = this.isVendor ? 'Vendor' : 'Customer';
    const otherRole = this.isVendor ? 'Customer' : 'Vendor';

    // Only the RECEIVER can edit the dispute section.
    if (this.rowsInitiatedBy && this.rowsInitiatedBy === otherRole) {
      return true;
    }

    return false;
  }

  toggleRowExpand(row: TimesheetRow) {
    row.isExpanded = !row.isExpanded;
  }

  toggleDispute(row: TimesheetRow) {
    if (this.isCompleted) return;

    // Initiator cannot dispute their own rows
    const myRole = this.isVendor ? 'Vendor' : 'Customer';
    const otherRole = this.isVendor ? 'Customer' : 'Vendor';
    if (this.rowsInitiatedBy === myRole) return;

    row.isDisputed = !row.isDisputed;
    row.status = row.isDisputed ? 'Disputed' : 'OK';

    if (row.isDisputed) {
      row.isExpanded = true;
      // Pre-fill correction values with original ones
      row.correctionStartTime = row.startTime || this.standardStartTime;
      row.correctionEndTime = row.endTime || this.standardEndTime;
      row.correctionBreakHours = row.breakHours || 0;
      row.correctionTotalHours = row.totalHours || 0;

      this.addToHistory(row, 'Disputed', `Marked as has problem by ${myRole}`);
    } else {
      row.disputeReason = '';
      row.correctionStartTime = null as any;
      row.correctionEndTime = null as any;
      row.correctionBreakHours = null as any;
      row.correctionTotalHours = null as any;
      row.isStartTimeDisputed = false;
      row.isEndTimeDisputed = false;
      row.isBreakDisputed = false;

      this.addToHistory(row, 'Undisputed', `Problem mark removed by ${myRole}`);
    }
  }

  toggleFieldDispute(
    row: TimesheetRow,
    field: 'startTime' | 'endTime' | 'break',
  ) {
    if (this.isCompleted) return;
    const myRole = this.isVendor ? 'Vendor' : 'Customer';
    const otherRole = this.isVendor ? 'Customer' : 'Vendor';
    if (this.rowsInitiatedBy === myRole) return;

    // Automatically enable dispute if not already
    if (!row.isDisputed) {
      this.toggleDispute(row);
    }

    if (field === 'startTime') {
      row.isStartTimeDisputed = !row.isStartTimeDisputed;
    } else if (field === 'endTime') {
      row.isEndTimeDisputed = !row.isEndTimeDisputed;
    } else if (field === 'break') {
      row.isBreakDisputed = !row.isBreakDisputed;
    }

    this.addToHistory(
      row,
      'Field Dispute Toggled',
      `${field} marked as ${row.isStartTimeDisputed || row.isEndTimeDisputed || row.isBreakDisputed ? 'problematic' : 'OK'}`,
    );
  }

  onValueChange(row: TimesheetRow, field: string, newValue: any) {
    this.calculateHours(row);
    this.addToHistory(row, 'Value Changed', `${field} updated to ${newValue}`);
  }

  get totalBookingDays(): number {
    if (!this.minDate || !this.maxDate) return 0;
    const s = new Date(this.minDate);
    const e = new Date(this.maxDate);
    const diff =
      Math.ceil((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    return diff > 0 ? diff : 0;
  }

  get canAddRow(): boolean {
    return this.rows.length < this.totalBookingDays && this.canEdit;
  }

  get bothAgreed(): boolean {
    return !!this.customerAgreed && !!this.vendorAgreed;
  }

  get myAgreed(): boolean {
    if (this.isCustomer) return !!this.customerAgreed;
    if (this.isVendor) return !!this.vendorAgreed;
    return false;
  }

  get otherPartyAgreed(): boolean {
    if (this.isCustomer) return !!this.vendorAgreed;
    if (this.isVendor) return !!this.customerAgreed;
    return false;
  }

  get canAgree(): boolean {
    // Can agree if timesheet has been submitted (formally initiated), no disputes, and timesheet is not completed
    if (this.isCompleted) return false;
    if (!this.rowsInitiatedBy) return false;
    if (this.hasDisputedRows) return false;
    if (this.timesheetStatus !== 'submitted') return false;
    return !this.myAgreed;
  }

  get canSign(): boolean {
    if (this.isCompleted) return false;
    if (!this.rowsInitiatedBy) return false;
    if (this.hasDisputedRows) return false;

    // Customer signs first, once they have agreed
    if (this.isCustomer) {
      return !!this.customerAgreed && !this.customerSignatureUrl;
    }

    // Vendor signs second, once customer has signed
    if (this.isVendor) {
      if (this.vendorSignatureUrl) return false;
      return !!this.customerSignatureUrl || !!this.customerSignatureBase64;
    }

    return false;
  }

  get isSignedView(): boolean {
    return (
      this.isCompleted &&
      !!this.customerSignatureUrl &&
      !!this.vendorSignatureUrl
    );
  }

  get hoursMatch(): boolean {
    const vTotal = this.vendorTotalHours;
    const cTotal = this.customerTotalHours;

    // If vendor has no rows but has entered verification hours, use that
    if (
      this.vendorRows.length === 0 &&
      this.vendorVerificationHours !== null &&
      this.vendorVerificationHours !== undefined
    ) {
      return Math.abs(this.vendorVerificationHours - cTotal) <= 0.01;
    }

    // If customer has no rows but has entered verification hours, use that
    if (
      this.customerRows.length === 0 &&
      this.customerVerificationHours !== null &&
      this.customerVerificationHours !== undefined
    ) {
      return Math.abs(vTotal - this.customerVerificationHours) <= 0.01;
    }

    return Math.abs(vTotal - cTotal) <= 0.01;
  }

  async completeTimesheet() {
    const data = this.data;
    if (!data) return;

    if (!this.hoursMatch) {
      this.ux.showError('Hours must match before submitting timesheet.');
      return;
    }

    // Check if both parties have already signed
    if (this.customerSignatureUrl && this.vendorSignatureUrl) {
      this.isCompleted = true;
      await this.save(true); // Finalize and close
      return;
    }

    // Check if current user has already signed
    const hasMySignature =
      (this.isVendor && this.vendorSignatureUrl) ||
      (this.isCustomer && this.customerSignatureUrl);
    if (hasMySignature) {
      // Notify other party that I have signed/submitted
      const booking = data.booking;
      const vendorId = booking?.equipment?.bywhom;
      const customerId = booking?.bywhom;

      if (this.isVendor && customerId) {
        this.notificationService.sendNotification(customerId, {
          title: 'Timesheet Approved by Vendor',
          message: `Vendor has approved and signed Timesheet ${this.tsNumber}. awaiting your signature.`,
          bookingId: data.bookingId,
          type: 'success', // or info
          link: '/booking-orders',
        });
      } else if (this.isCustomer && vendorId) {
        // Customer notification is handled in 'save' usually, but good to ensure here too if flow differs
        this.notificationService.sendNotification(vendorId, {
          title: 'Timesheet Signed by Customer',
          message: `Customer has signed Timesheet ${this.tsNumber}.`,
          bookingId: data.bookingId,
          type: 'success',
          link: '/business/bookings',
        });
      }

      this.ux.showSuccess(
        'Awaiting other party signature to complete.',
        'Signature Received',
      );
      this.onClose(true); // Close as draft for now, other party will finalize
      return;
    }

    // Hours match and user hasn't signed yet - proceed to show signature section
    this.ux.showSuccess(
      'Please provide your signature below to submit.',
      'Signature Required',
    );
  }

  // Conflict flagging removed in favor of unified table

  getCurrentRole(): 'Vendor' | 'Customer' {
    if (this.isVendor) return 'Vendor';
    if (this.isCustomer) return 'Customer';
    return 'Vendor'; // Default fallback
  }

  addRow() {
    if (!this.canAddRow) return;

    // Find the next missing date in the range
    let nextDateStr = this.minDate;
    if (this.minDate && this.maxDate) {
      const s = new Date(this.minDate);
      const e = new Date(this.maxDate);

      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        const dStr = this.formatDisplayDate(d);
        if (!this.rows.some((r) => r.date === dStr)) {
          nextDateStr = dStr;
          break;
        }
      }
    }

    this.rows.push({
      date: nextDateStr,
      startTime: this.standardStartTime,
      endTime: this.standardEndTime,
      breakHours: 0,
      totalHours: 0, // Will be calculated below
      addedBy: this.getCurrentRole(),
    });
    this.calculateHours(this.rows[this.rows.length - 1]);
  }

  private checkInitiationTakeover() {
    // If we're starting to generate rows but there's an existing draft from the other party
    // that hasn't been sent, we clear it to start our own initiation.
    if (!this.rowsInitiatedBy && this.rows.length > 0 && !this.isMyDraft) {
      this.rows = [];
    }
  }

  generateBatchRows() {
    if (!this.canEdit) return;
    this.checkInitiationTakeover();

    const start = this.parseLocalDate(this.batchStartDate);
    const end = this.parseLocalDate(this.batchEndDate);

    this.processBatch(
      start,
      end,
      this.batchStartTime,
      this.batchEndTime,
      this.batchBreakHours,
      true,
    );
  }

  autoFillStandard() {
    if (!this.canEdit) return;

    if (!this.standardStartTime || !this.standardEndTime) {
      this.ux.showError('Please set standard working hours.');
      return;
    }

    // Simple time string comparison (HH:mm) works for 24h format
    if (this.standardStartTime >= this.standardEndTime) {
      this.ux.showError('Start time must be before end time.');
      return;
    }

    this.checkInitiationTakeover();

    // Use the booking's start/end dates which were already initialized
    const start = this.parseLocalDate(this.batchStartDate);
    const end = this.parseLocalDate(this.batchEndDate);

    if (!start || !end) {
      this.ux.showError('Booking date range not found.');
      return;
    }

    // Pass the selection
    this.processBatch(
      start,
      end,
      this.standardStartTime,
      this.standardEndTime,
      this.standardBreakHours,
      true,
    );
  }

  private parseLocalDate(isoStr: string): Date {
    if (!isoStr) return new Date();
    const [y, m, d] = isoStr.split('-').map(Number);
    return new Date(y, m - 1, d, 0, 0, 0, 0); // Local midnight
  }

  processBatch(
    start: Date,
    end: Date,
    startTime: string,
    endTime: string,
    breakHours: number,
    useRefinedDays: boolean = false,
  ) {
    const myRole = this.getCurrentRole();

    // 1. Clear existing rows for this role to ensure the final timesheet
    // exactly matches the user's batch setup selections (overwriting previous attempts)
    this.rows = this.rows.filter((r) => (r.addedBy || 'Vendor') !== myRole);

    // Normalize both to midnight for robust comparison
    const normStart = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate(),
    );
    const normEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate());

    // Iterate from start to end
    for (
      let d = new Date(normStart);
      d <= normEnd;
      d.setDate(d.getDate() + 1)
    ) {
      // Refined Day Logic
      if (useRefinedDays) {
        const dayIndex = d.getDay(); // 0-6
        if (!this.weekdaysSelection[dayIndex]) {
          continue; // Skip if day is unchecked
        }
      }

      const dateStr = this.formatDisplayDate(d);
      const myRole = this.getCurrentRole();

      // Skip if this date is in the excluded list
      if (this.excludedDates.includes(this.toLocalISOString(d))) {
        continue;
      }

      // Add row
      this.rows.push({
        date: dateStr,
        startTime: startTime,
        endTime: endTime,
        breakHours: breakHours,
        totalHours: 0, // Will calculate
        addedBy: myRole,
      });
      this.calculateHours(this.rows[this.rows.length - 1]);
    }

    // Final Sort by Date
    this.rows.sort((a, b) => {
      const da = this.parseLocalDateDisplay(a.date);
      const db = this.parseLocalDateDisplay(b.date);
      return (da?.getTime() || 0) - (db?.getTime() || 0);
    });

    this.ux.showSuccess('Entries generated successfully.');
    this.showBatchSetup = false;
  }

  private parseLocalDateDisplay(displayStr: string): Date | null {
    if (!displayStr) return null;
    const parts = displayStr.split('/');
    if (parts.length !== 3) return null;
    const [d, m, y] = parts.map(Number);
    return new Date(y, m - 1, d);
  }

  // Unified table uses signature flow directly without split-total verification

  removeRow(row: TimesheetRow) {
    if (!this.canEdit || this.isCompleted) return;

    // Once formal initiation has occurred, rows cannot be deleted
    if (this.rowsInitiatedBy) {
      this.ux.showError(
        'Records cannot be deleted once the timesheet has been initiated.',
      );
      return;
    }

    const index = this.rows.indexOf(row);
    if (index > -1) {
      this.rows.splice(index, 1);
    }
  }

  clearRows() {
    if (!this.canEdit) return;
    const myRole = this.getCurrentRole();
    // Remove only rows added by ME
    this.rows = this.rows.filter((r) => (r.addedBy || 'Vendor') !== myRole);
    this.ux.showSuccess('All your entries have been cleared.');
  }

  calculateHours(row: TimesheetRow) {
    if (this.isCompleted) return;
    if (!row.startTime || !row.endTime) return;

    const start = this.parseTime(row.startTime);
    const end = this.parseTime(row.endTime);

    if (start && end) {
      let diff = (end.getTime() - start.getTime()) / (1000 * 60 * 60); // Hours
      if (diff < 0) diff += 24; // Handle overnight

      const breakHours = row.breakHours || 0;
      row.totalHours = Math.max(0, parseFloat((diff - breakHours).toFixed(2)));
    }

    // Handle corrections if any
    if (row.correctionStartTime && row.correctionEndTime) {
      const cStart = this.parseTime(row.correctionStartTime);
      const cEnd = this.parseTime(row.correctionEndTime);
      if (cStart && cEnd) {
        let cDiff = (cEnd.getTime() - cStart.getTime()) / (1000 * 60 * 60);
        if (cDiff < 0) cDiff += 24;
        const cBreak = row.correctionBreakHours || 0;
        row.correctionTotalHours = Math.max(
          0,
          parseFloat((cDiff - cBreak).toFixed(2)),
        );

        // Mark as corrected if it's different from original and has correction
        // Auto-dispute if anything changes in the correction fields
        const isDifferent =
          row.startTime !== row.correctionStartTime ||
          row.endTime !== row.correctionEndTime ||
          row.breakHours !== row.correctionBreakHours;

        if (isDifferent) {
          if (!row.isDisputed) {
            row.isDisputed = true;
            row.isExpanded = true;
            this.addToHistory(
              row,
              'Correction Entered',
              `Receiver entered a change.`,
            );
          }
          row.status = 'Changed';
        } else if (row.isDisputed) {
          row.status = 'Resolved';
        }
      }
    }
  }

  addToHistory(row: TimesheetRow, action: string, details?: string) {
    if (!row.history) row.history = [];
    const myRole = this.isVendor ? 'Vendor' : 'Customer';
    const myName = myRole === 'Vendor' ? this.vendorName : this.customerName;

    row.history.push({
      timestamp: new Date().toISOString(),
      user: myName || myRole,
      role: myRole,
      action: action,
      details: details,
    });
  }

  parseTime(timeStr: string): Date | null {
    if (!timeStr) return null;
    const [hours, minutes] = timeStr.split(':').map(Number);
    const d = new Date();
    d.setHours(hours, minutes, 0, 0);
    return d;
  }

  getLastUpdate(row: TimesheetRow) {
    if (!row.history || row.history.length === 0) return null;
    return row.history[row.history.length - 1];
  }

  get fullHistory(): TimesheetHistoryEntry[] {
    const allLogs: TimesheetHistoryEntry[] = [];
    this.rows.forEach((r) => {
      if (r.history) {
        r.history.forEach((log) => {
          allLogs.push({
            ...log,
            rowDate: r.date, // Ensure row date is attached
          });
        });
      }
    });

    // Sort by timestamp descending
    return allLogs.sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
  }

  getTotalHours(): number {
    return this.rows.reduce((sum, r) => sum + (r.totalHours || 0), 0);
  }

  get vendorTotalHours(): number {
    return this.getTotalHours();
  }

  get customerTotalHours(): number {
    return this.getTotalHours();
  }

  async save(close: boolean = true, forceSend: boolean = false) {
    const data = this.data;
    if (!data) return;

    if (forceSend && !this.rowsInitiatedBy) {
      this.rowsInitiatedBy = this.isVendor ? 'Vendor' : 'Customer';
      this.timesheetStatus = 'submitted';
    }

    // Validate conflicts before saving

    const conflict = this.hasDateConflict();
    if (conflict) {
      this.ux.showError(
        `Date conflict detected on ${conflict}. Please select a different date.`,
      );
      return;
    }

    this.isLoading = true;
    try {
      const docRef = doc(
        this.afs,
        `booking-list/${data.bookingId}/timesheets/main`,
      );

      const isNowCompleted =
        this.isCompleted ||
        (!!this.customerSignatureUrl && !!this.vendorSignatureUrl);
      if (isNowCompleted) this.timesheetStatus = 'completed';

      // Deep clone rows and clean out undefined values to avoid serialization issues
      const cleanRows = JSON.parse(JSON.stringify(this.rows, (key, value) => {
        return value === undefined ? null : value;
      }));

      const dataToSave: any = {
        rows: cleanRows,
        customerSignatureUrl: this.customerSignatureUrl || null,
        customerSignatureBase64: this.customerSignatureBase64 || null,
        vendorSignatureUrl: this.vendorSignatureUrl || null,
        vendorSignatureBase64: this.vendorSignatureBase64 || null,
        vendorVerificationHours: this.vendorVerificationHours || null,
        customerVerificationHours: this.customerVerificationHours || null,
        isCompleted: isNowCompleted,
        timesheetStatus: this.timesheetStatus,
        customerRejectionComment: this.customerRejectionComment || null,
        customerAgreed: this.customerAgreed || false,
        vendorAgreed: this.vendorAgreed || false,
        updatedAt: new Date().toISOString(),
        totalHours: this.getTotalHours(),
        bookingId: data.bookingId,
        rowsInitiatedBy: this.rowsInitiatedBy || null,
        quotationNumber: data.booking?.quotationNumber ?? null,
      };

      // 1. Update Firestore (Historical/Official)
      await setDoc(docRef, dataToSave, { merge: true });

      // 2. Update Realtime Database (Live Collaboration)
      await this.rtBooking.updateTimesheet(
        data.bookingId,
        {
          ...dataToSave,
          updatedAt: Date.now(), // RTDB prefers number timestamps
        },
        this.auth.currentUser?.uid,
      );

      // Update local state
      this.isCompleted = dataToSave.isCompleted;

      // Update main booking document with uploaded timesheet URL if present
      // We do this regardless of 'close' because upload dialog calls save(false)
      if (this.uploadedTimesheetUrl) {
        const bookingRef = doc(this.afs, `booking-list/${data.bookingId}`);
        await updateDoc(bookingRef, {
          uploadedTimesheetUrl: this.uploadedTimesheetUrl,
        });

        // Mirror to RTDB root for list views
        await this.rtBooking.updateInvoice(
          data.bookingId,
          {
            uploadedTimesheetUrl: this.uploadedTimesheetUrl,
          },
          this.auth.currentUser?.uid,
        );
      }

      if (close) {
        this.ux.showSuccess('Timesheet saved successfully!');

        const booking = data.booking;
        const currentUser = this.auth.currentUser?.uid;
        const vendorId = booking?.equipment?.bywhom;
        const customerId = booking?.bywhom;

        // Real-time Notification for Completion
        if (this.isCompleted) {
          const notification = {
            title: 'Timesheet Completed',
            message: `Timesheet ${this.tsNumber} has been signed and completed.`,
            bookingId: data.bookingId,
            type: 'success' as const,
          };

          if (currentUser === vendorId && customerId) {
            this.notificationService.sendNotification(customerId, notification);
          } else if (currentUser === customerId && vendorId) {
            this.notificationService.sendNotification(vendorId, notification);
          } else {
            if (customerId)
              this.notificationService.sendNotification(
                customerId,
                notification,
              );
            if (vendorId)
              this.notificationService.sendNotification(vendorId, notification);
          }
        }
        // Notification for Update/Submission (if not completed yet)
        else if (this.isCustomer && vendorId) {
          const notification = {
            title: 'Timesheet Updated',
            message: `Customer has updated/submitted Timesheet ${this.tsNumber}.`,
            bookingId: data.bookingId,
            type: 'info' as const,
          };
          this.notificationService.sendNotification(vendorId, notification);
        } else if (this.isVendor && customerId) {
          const notification = {
            title: 'Timesheet Uploaded by Vendor',
            message: `Vendor has uploaded the timesheet. Please review it and if required make changes in them.`,
            bookingId: data.bookingId,
            type: 'info' as const,
          };
          this.notificationService.sendNotification(customerId, notification);
        }

        this.onClose(true);
      }
    } catch (error) {
      console.error('Error saving timesheet:', error);
      this.ux.showError('Failed to save timesheet.');
    } finally {
      this.isLoading = false;
    }
  }

  // ==========================================
  // TIMESHEET REVIEW FLOW
  // ==========================================

  async agreeToTimesheet() {
    if (this.isCompleted || !this.rowsInitiatedBy || this.hasDisputedRows) return;
    const data = this.data;
    if (!data) return;

    this.isLoading = true;
    try {
      const updatePayload: any = {};
      if (this.isCustomer) {
        this.customerAgreed = true;
        updatePayload.customerAgreed = true;
      } else if (this.isVendor) {
        this.vendorAgreed = true;
        updatePayload.vendorAgreed = true;
      }

      const docRef = doc(this.afs, `booking-list/${data.bookingId}/timesheets/main`);
      await setDoc(docRef, updatePayload, { merge: true });
      await this.rtBooking.updateTimesheet(data.bookingId, updatePayload, this.auth.currentUser?.uid);

      // Notify the other party
      const vendorId = data.booking?.equipment?.bywhom || data.booking?.vendorId;
      const customerId = data.booking?.bywhom;

      if (this.isCustomer && vendorId) {
        this.notificationService.sendNotification(vendorId, {
          title: 'Customer Agreed to Timesheet',
          message: `Customer has agreed to the timesheet for booking #${data.bookingId}. Please review and agree to unlock signatures.`,
          bookingId: data.bookingId,
          type: 'info' as const,
          link: '/business/bookings',
        });
      } else if (this.isVendor && customerId) {
        this.notificationService.sendNotification(customerId, {
          title: 'Vendor Agreed to Timesheet',
          message: `Vendor has agreed to the timesheet for booking #${data.bookingId}. Both parties have agreed — please proceed to sign.`,
          bookingId: data.bookingId,
          type: 'success' as const,
          link: '/booking-orders',
        });
      }

      this.ux.showSuccess(
        this.bothAgreed
          ? 'Both parties agreed! You can now proceed to sign.'
          : 'Your agreement has been recorded. Waiting for the other party to agree.',
        'Agreement Recorded'
      );
    } catch (err) {
      console.error('Error recording agreement:', err);
      this.ux.showError('Failed to record agreement.');
    } finally {
      this.isLoading = false;
    }
  }

  async withdrawAgreement() {
    if (this.isCompleted || this.customerSignatureUrl || this.vendorSignatureUrl) return;
    const data = this.data;
    if (!data) return;

    this.isLoading = true;
    try {
      const updatePayload: any = {};
      if (this.isCustomer) {
        this.customerAgreed = false;
        updatePayload.customerAgreed = false;
      } else if (this.isVendor) {
        this.vendorAgreed = false;
        updatePayload.vendorAgreed = false;
      }

      const docRef = doc(this.afs, `booking-list/${data.bookingId}/timesheets/main`);
      await setDoc(docRef, updatePayload, { merge: true });
      await this.rtBooking.updateTimesheet(data.bookingId, updatePayload, this.auth.currentUser?.uid);

      this.ux.showSuccess('Agreement withdrawn.');
    } catch (err) {
      console.error('Error withdrawing agreement:', err);
      this.ux.showError('Failed to withdraw agreement.');
    } finally {
      this.isLoading = false;
    }
  }

  async sendChanges() {
    this.customerRejectionComment = 'Corrections suggested on timesheet entries.';
    this.timesheetStatus = 'rejected';
    // Reset both agreements since the timesheet is going back to negotiation
    this.customerAgreed = false;
    this.vendorAgreed = false;

    const data = this.data;
    if (!data) return;

    this.isLoading = true;
    try {
      const docRef = doc(this.afs, `booking-list/${data.bookingId}/timesheets/main`);
      await setDoc(docRef, {
        timesheetStatus: 'rejected',
        customerRejectionComment: this.customerRejectionComment,
        customerAgreed: false,
        vendorAgreed: false,
        rows: this.rows,
        updatedAt: new Date(),
      }, { merge: true });

      await this.rtBooking.updateTimesheet(data.bookingId, {
        timesheetStatus: 'rejected',
        customerRejectionComment: this.customerRejectionComment,
        customerAgreed: false,
        vendorAgreed: false,
        rows: this.rows,
      }, this.auth.currentUser?.uid);

      // Notify vendor
      const vendorId = data.booking?.equipment?.bywhom || data.booking?.vendorId;
      if (vendorId) {
        this.notificationService.sendNotification(vendorId, {
          title: 'Timesheet Changes Requested',
          message: `Customer has requested modifications to the timesheet. Please review and resubmit.`,
          bookingId: data.bookingId,
          type: 'warning' as const,
        });
      }

      this.ux.showSuccess('Changes sent to vendor.');
    } catch (err) {
      console.error('Error sending changes:', err);
      this.ux.showError('Failed to send changes.');
    } finally {
      this.isLoading = false;
    }
  }

  acceptRowCorrection(row: TimesheetRow) {
    if (!row.isDisputed) return;
    // Apply corrections to main values
    if (row.correctionStartTime) row.startTime = row.correctionStartTime;
    if (row.correctionEndTime) row.endTime = row.correctionEndTime;
    if (row.correctionBreakHours !== undefined) row.breakHours = row.correctionBreakHours;
    this.calculateHours(row);
    // Clear dispute
    row.isDisputed = false;
    row.status = 'Resolved';
    row.correctionStartTime = undefined as any;
    row.correctionEndTime = undefined as any;
    row.correctionBreakHours = undefined as any;
    row.correctionTotalHours = undefined as any;
    row.isExpanded = false;
    this.addToHistory(row, 'Correction Accepted', 'Vendor accepted customer correction');
  }

  rejectRowCorrection(row: TimesheetRow) {
    if (!row.isDisputed) return;
    // Keep original values — just clear the dispute
    row.isDisputed = false;
    row.status = 'OK';
    row.correctionStartTime = undefined as any;
    row.correctionEndTime = undefined as any;
    row.correctionBreakHours = undefined as any;
    row.correctionTotalHours = undefined as any;
    row.isExpanded = false;
    this.addToHistory(row, 'Correction Rejected', 'Vendor kept original values');
  }

  activeReviewRow?: TimesheetRow;
  @ViewChild('acceptCorrectionModal') acceptCorrectionModal!: TemplateRef<any>;
  @ViewChild('keepOriginalModal') keepOriginalModal!: TemplateRef<any>;
  private correctionDialogRef: any;

  openAcceptCorrectionModal(row: TimesheetRow) {
    this.activeReviewRow = row;
    this.correctionDialogRef = this.dialog.open(this.acceptCorrectionModal, {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      panelClass: 'full-page-dialog'
    });
  }

  openKeepOriginalModal(row: TimesheetRow) {
    this.activeReviewRow = row;
    this.correctionDialogRef = this.dialog.open(this.keepOriginalModal, {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      panelClass: 'full-page-dialog'
    });
  }

  async confirmAcceptCorrection() {
    if (!this.activeReviewRow) return;
    this.acceptRowCorrection(this.activeReviewRow);
    this.correctionDialogRef?.close();

    const data = this.data;
    if (!data) return;

    // Resubmit — this changes status back to submitted and notifies customer
    await this.resubmitTimesheet();

    // Extra notification: changes accepted
    const customerId = data.booking?.bywhom;
    if (customerId) {
      this.notificationService.sendNotification(customerId, {
        title: 'Vendor Accepted Your Changes',
        message: `The vendor has accepted your suggested corrections and resubmitted the timesheet. Please review, agree, and sign.`,
        bookingId: data.bookingId,
        type: 'success' as const,
      });
    }
  }

  async confirmKeepOriginal() {
    if (!this.activeReviewRow) return;
    this.rejectRowCorrection(this.activeReviewRow);
    this.correctionDialogRef?.close();

    const data = this.data;
    if (!data) return;

    // Resubmit — this changes status back to submitted
    await this.resubmitTimesheet();

    // Extra notification: changes rejected
    const customerId = data.booking?.bywhom;
    if (customerId) {
      this.notificationService.sendNotification(customerId, {
        title: 'Changes Rejected by Vendor',
        message: `The vendor has reviewed your suggested correction but kept the original values. The timesheet has been resubmitted. Please review and sign.`,
        bookingId: data.bookingId,
        type: 'warning' as const,
      });
    }
  }

  isFieldUpdated(field: 'startTime' | 'endTime' | 'breakHours', row?: TimesheetRow): boolean {
    if (!row) return false;
    if (field === 'startTime') {
      return !!row.correctionStartTime && row.correctionStartTime !== row.startTime;
    }
    if (field === 'endTime') {
      return !!row.correctionEndTime && row.correctionEndTime !== row.endTime;
    }
    if (field === 'breakHours') {
      return row.correctionBreakHours !== undefined && row.correctionBreakHours !== row.breakHours;
    }
    return false;
  }

  isTotalHoursUpdated(row?: TimesheetRow): boolean {
    if (!row) return false;
    return row.correctionTotalHours !== undefined && row.correctionTotalHours !== row.totalHours;
  }

  async resubmitTimesheet() {
    // Clear all remaining disputes before resubmit
    this.rows.forEach(r => { if (r.isDisputed) this.rejectRowCorrection(r); });
    this.timesheetStatus = 'submitted';
    this.customerRejectionComment = '';
    // Reset both agreements so both parties must freshly agree to the updated timesheet
    this.customerAgreed = false;
    this.vendorAgreed = false;
    await this.save(false, false);

    const data = this.data;
    if (!data) return;
    const customerId = data.booking?.bywhom;
    if (customerId) {
      this.notificationService.sendNotification(customerId, {
        title: 'Timesheet Resubmitted by Vendor',
        message: 'Vendor has reviewed your feedback and resubmitted the timesheet. Please review, agree, and sign.',
        bookingId: data.bookingId,
        type: 'info' as const,
      });
    }
    this.ux.showSuccess('Timesheet resubmitted successfully!');
  }

  createNewTimesheet() {
    this.rows = [];
    this.rowsInitiatedBy = undefined;
    this.timesheetStatus = 'draft';
    this.customerRejectionComment = '';
    this.customerSignatureUrl = undefined;
    this.customerSignatureBase64 = undefined;
    this.vendorSignatureUrl = undefined;
    this.vendorSignatureBase64 = undefined;
    this.isCompleted = false;
    this.showInitialChoice = true;
    this.addToHistory(this.rows[0] || {} as any, 'New Timesheet', 'Vendor created a new timesheet');
    this.ux.showSuccess('New timesheet started. Please fill in the entries.');
  }

  isImage(url: string | null): boolean {
    if (!url) return false;
    const lowUrl = url.toLowerCase();
    return (
      lowUrl.includes('.png') ||
      lowUrl.includes('.jpg') ||
      lowUrl.includes('.jpeg') ||
      lowUrl.includes('.webp') ||
      lowUrl.includes('image')
    );
  }

  onGenerateClick() {
    this.showInitialChoice = false;
    this.showBatchSetup = true;
    this.setupStep = 1;
  }

  onBack() {
    if (
      !this.showInitialChoice &&
      !this.showBatchSetup &&
      !this.showUploadSection
    ) {
      // If we are in the main table view, and it's still a draft, go back to setup
      if (
        !this.rowsInitiatedBy &&
        !this.isCompleted &&
        !this.uploadedTimesheetUrl
      ) {
        this.showBatchSetup = true;
      } else {
        this.onClose();
      }
    } else {
      this.onClose();
    }
  }

  onBackToChoice() {
    this.showInitialChoice = true;
    this.showUploadSection = false;
    this.showBatchSetup = false;
    this.setupStep = 1;
    this.selectedFile = null;
  }

  openUploadSection() {
    this.showInitialChoice = false;
    this.showUploadSection = true;
  }

  openFilePicker() {
    this.fileInput.nativeElement.click();
  }

  nextStep() {
    if (this.setupStep < 3) this.setupStep++;
  }

  prevStep() {
    if (this.setupStep > 1) {
      this.setupStep--;
    } else {
      this.onBackToChoice();
    }
  }

  addOffDate() {
    if (this.tempOffDate && !this.excludedDates.includes(this.tempOffDate)) {
      this.excludedDates.push(this.tempOffDate);
      this.tempOffDate = '';
    }
  }

  removeOffDate(date: string) {
    this.excludedDates = this.excludedDates.filter((d) => d !== date);
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    if (!(file.type.includes('pdf') || file.type.includes('image'))) {
      this.ux.showError('Please select a PDF or image file.');
      return;
    }

    this.selectedFile = file;
  }

  async uploadTimesheetFile() {
    if (!this.selectedFile) {
      this.ux.showError('Please select a file to upload.');
      return;
    }

    this.isSaving = true;
    try {
      const storage = getStorage();
      const filePath = `timesheets/${this.data!.bookingId}/${Date.now()}_${this.selectedFile.name}`;
      const fileRef = ref(storage, filePath);
      const uploadResult = await uploadBytes(fileRef, this.selectedFile);
      const downloadURL = await getDownloadURL(uploadResult.ref);

      this.uploadedTimesheetUrl = downloadURL;

      // Update Realtime DB
      await this.rtBooking.updateTimesheet(
        this.data!.bookingId,
        { uploadedTimesheetUrl: downloadURL },
        this.auth.currentUser?.uid,
      );

      // Update Firestore
      const bookingRef = doc(this.afs, `booking-list/${this.data!.bookingId}`);
      await updateDoc(bookingRef, {
        uploadedTimesheetUrl: downloadURL,
        quotationNumber: this.data!.booking?.quotationNumber ?? null,
      });

      this.ux.openSnackBar('Timesheet uploaded successfully!', 'OK');
      this.showUploadSection = false;
      this.showInitialChoice = false;

      // Also update the main booking object in data
      if (this.data && this.data.booking) {
        this.data.booking.uploadedTimesheetUrl = downloadURL;
      }
    } catch (err) {
      console.error('❌ Upload error:', err);
      this.ux.showError('Failed to upload timesheet.');
    } finally {
      this.isSaving = false;
    }
  }

  // isImage(url: string): boolean {
  //   if (!url) return false;
  //   return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(url);
  // }

  close() {
    this.onClose();
  }

  openUploadDialog() {
    const title = 'Upload Timesheet';
    const existingUrl = this.uploadedTimesheetUrl;
    const storagePathBase = `documents/${this.data!.bookingId}`;
    const filePrefix = 'timesheet';

    const dialogRef = this.dialog.open(UploadDocumentDialogComponent, {
      width: '500px',
      data: { title, existingUrl, storagePathBase, filePrefix },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.uploadedUrl) {
        this.uploadedTimesheetUrl = result.uploadedUrl;

        // Notify Vendor if uploaded by Customer
        if (this.isCustomer) {
          const vendorId = this.data!.booking.equipment?.bywhom;
          if (vendorId) {
            const notification = {
              title: 'Timesheet Uploaded',
              message: `Customer has uploaded a timesheet for Booking #${this.data!.bookingId}`,
              type: 'success' as const,
              link: '/business/bookings',
            };
            this.notificationService.sendNotification(vendorId, notification);
          }
        }
      }
    });
  }

  // ==========================================
  // SIGNATURE LOGIC (Copied from Template)
  // ==========================================

  triggerSigInput() {
    document.getElementById('tsSigInput')?.click();
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }

  async onSigSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      try {
        this.ux.showOverlay('Uploading Signature...');
        // Convert to Base64 for instant PDF use
        const base64 = await this.fileToBase64(file);
        if (this.isVendor) {
          this.vendorSignatureBase64 = base64;
          this.vendorSignatureUrl = base64; // Temporarily use base64 for instant preview
        } else if (this.isCustomer) {
          this.customerSignatureBase64 = base64;
          this.customerSignatureUrl = base64; // Temporarily use base64 for instant preview
        }

        const storage = getStorage();
        const path = `signatures/${this.data!.bookingId}_${Date.now()}_${file.name}`;
        const storageRef = ref(storage, path);
        const uploadResult = await uploadBytes(storageRef, file);
        const url = await getDownloadURL(uploadResult.ref);

        // Assign to appropriate signature field based on role
        if (this.isVendor) {
          this.vendorSignatureUrl = url;
          this.vendorSignatureBase64 = base64;
        } else if (this.isCustomer) {
          this.customerSignatureUrl = url;
          this.customerSignatureBase64 = base64;
        }
        await this.save(false, false);
      } catch (error) {
        console.error('Error uploading signature:', error);
        this.ux.showError('Error uploading signature');
      } finally {
        this.ux.closeOverlay();
      }
    }
  }

  initCanvas() {
    if (this.sigCanvas) {
      const canvas = this.sigCanvas.nativeElement;
      this.ctx = canvas.getContext('2d')!;
      this.ctx.lineWidth = 2;
      this.ctx.lineCap = 'round';
      this.ctx.strokeStyle = '#000000';
    }
  }

  startDrawing(event: MouseEvent) {
    if (!this.ctx) this.initCanvas();
    this.isDrawing = true;
    this.ctx.beginPath();
    this.ctx.moveTo(event.offsetX, event.offsetY);
  }

  draw(event: MouseEvent) {
    if (!this.isDrawing) return;
    this.ctx.lineTo(event.offsetX, event.offsetY);
    this.ctx.stroke();
  }

  stopDrawing() {
    this.isDrawing = false;
    this.ctx.closePath();
  }

  clearCanvas() {
    if (this.sigCanvas) {
      const canvas = this.sigCanvas.nativeElement;
      this.ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }

  async saveCanvasSignature() {
    if (!this.sigCanvas) return;
    const canvas = this.sigCanvas.nativeElement;

    this.ux.showOverlay('Saving Signature...');

    // Get base64 directly from canvas
    const base64 = canvas.toDataURL('image/png');
    if (this.isVendor) {
      this.vendorSignatureBase64 = base64;
      this.vendorSignatureUrl = base64;
    } else if (this.isCustomer) {
      this.customerSignatureBase64 = base64;
      this.customerSignatureUrl = base64;
    }

    canvas.toBlob(async (blob) => {
      if (blob) {
        try {
          const storage = getStorage();
          const path = `signatures/${this.data!.bookingId}_drawn_${Date.now()}.png`;
          const storageRef = ref(storage, path);
          const uploadResult = await uploadBytes(storageRef, blob);
          const url = await getDownloadURL(uploadResult.ref);
          // Assign to appropriate signature field based on role
          if (this.isVendor) {
            this.vendorSignatureUrl = url;
            this.vendorSignatureBase64 = base64;
          } else if (this.isCustomer) {
            this.customerSignatureUrl = url;
            this.customerSignatureBase64 = base64;
          }
          await this.save(false, false);
        } catch (error) {
          console.error('Error saving drawn signature:', error);
          this.ux.showError('Error saving signature');
        } finally {
          this.ux.closeOverlay();
        }
      }
    }, 'image/png');
  }
  async downloadPDF() {
    const element = document.getElementById('timesheet-pdf-content');
    if (!element) {
      this.ux.showError('Timesheet content not found.');
      return;
    }

    this.ux.showOverlay('Generating PDF...');

    // 1. Pre-process signatures and logos to Base64 to bypass CORS in capture
    const originalCustomerSig = this.customerSignatureUrl;
    const originalVendorSig = this.vendorSignatureUrl;
    const originalVendorLogo = this.vendorDetails?.logoUrl;
    const originalCustomerLogo = this.customerDetails?.logoUrl;

    try {
      if (this.customerSignatureBase64) {
        this.customerSignatureUrl = this.customerSignatureBase64;
      }
      if (this.vendorSignatureBase64) {
        this.vendorSignatureUrl = this.vendorSignatureBase64;
      }
      if (this.vendorDetails?.logoBase64) {
        this.vendorDetails.logoUrl = this.vendorDetails.logoBase64;
      }
      if (this.customerDetails?.logoBase64) {
        this.customerDetails.logoUrl = this.customerDetails.logoBase64;
      }

      // Wait a bit for DOM to update
      await new Promise((resolve) => setTimeout(resolve, 800));

      // Capture the element using html2canvas with settings identical to LPO
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
        allowTaint: true,
        imageTimeout: 15000,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      // First page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add more pages if content spills over (Multi-page logic)
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const fileName = `Timesheet-${this.tsNumber || this.data!.bookingId.slice(-6).toUpperCase()}.pdf`;
      pdf.save(fileName);

      this.ux.showSuccess('PDF Downloaded successfully!', 'Downloaded');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      this.ux.showError('Failed to generate PDF download.');
    } finally {
      // Restore original URLs
      this.customerSignatureUrl = originalCustomerSig;
      this.vendorSignatureUrl = originalVendorSig;
      if (this.vendorDetails) this.vendorDetails.logoUrl = originalVendorLogo;
      if (this.customerDetails)
        this.customerDetails.logoUrl = originalCustomerLogo;

      this.ux.closeOverlay();
    }
  }

  get currentMonthName(): string {
    return new Intl.DateTimeFormat('en-US', { month: 'long' }).format(
      new Date(this.currentYear, this.currentMonth),
    );
  }

  // --- CUSTOM CALENDAR LOGIC ---

  openCalendar(
    event: Event,
    row: TimesheetRow | 'off-day' | 'batch-start' | 'batch-end',
  ) {
    event.stopPropagation();
    if (typeof row === 'string') {
      this.calendarMode = row;
      this.activeCalendarRow = null;
      this.showWizardCalendar = true;

      let initialDate: Date;
      if (row === 'off-day' && this.tempOffDate) initialDate = new Date(this.tempOffDate);
      else if (row === 'batch-start' && this.batchStartDate) initialDate = new Date(this.batchStartDate);
      else if (row === 'batch-end' && this.batchEndDate) initialDate = new Date(this.batchEndDate);
      else if (this.minDate) initialDate = new Date(this.minDate);
      else initialDate = new Date();

      this.currentMonth = initialDate.getMonth();
      this.currentYear = initialDate.getFullYear();
    } else {
      this.calendarMode = 'row-date';
      this.activeCalendarRow = row;
      this.showWizardCalendar = false;

      // If row has a date, sync calendar to that date
      const rowDateStr = row.date;
      if (rowDateStr) {
        const parts = rowDateStr.split('/');
        if (parts.length === 3) {
          const [d, m, y] = parts.map(Number);
          this.currentMonth = m - 1;
          this.currentYear = y;
        }
      } else {
        const today = new Date();
        this.currentMonth = today.getMonth();
        this.currentYear = today.getFullYear();
      }
    }

    this.generateCalendar();
  }

  closeCalendar() {
    this.activeCalendarRow = null;
    this.showWizardCalendar = false;
  }

  formatDisplayDate(date: Date): string {
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = date.getFullYear();
    return `${d}/${m}/${y}`;
  }

  generateCalendar() {
    const days: any[] = [];
    const firstDay = new Date(this.currentYear, this.currentMonth, 1).getDay();
    const lastDate = new Date(
      this.currentYear,
      this.currentMonth + 1,
      0,
    ).getDate();

    const today = new Date();
    const todayDate = today.getDate();
    const todayMonth = today.getMonth();
    const todayYear = today.getFullYear();

    // Empty leading cells
    for (let i = 0; i < firstDay; i++) {
      days.push({ date: '', inactive: true, isToday: false, isPast: false });
    }

    // Actual days
    for (let d = 1; d <= lastDate; d++) {
      const isToday =
        this.currentYear === todayYear &&
        this.currentMonth === todayMonth &&
        d === todayDate;

      const isPast =
        this.currentYear < todayYear ||
        (this.currentYear === todayYear && this.currentMonth < todayMonth) ||
        (this.currentYear === todayYear &&
          this.currentMonth === todayMonth &&
          d < todayDate);

      const dateObj = new Date(this.currentYear, this.currentMonth, d);
      const isLocked = this.isDateLocked(dateObj);

      // Check if within minDate/maxDate range - Use local parsing to avoid timezone shifts
      let isOutOfRange = false;
      if (this.minDate) {
        const min = this.parseLocalDate(this.minDate);
        min.setHours(0, 0, 0, 0);
        if (dateObj < min) isOutOfRange = true;
      }
      if (this.maxDate) {
        const max = this.parseLocalDate(this.maxDate);
        max.setHours(23, 59, 59, 999);
        if (dateObj > max) isOutOfRange = true;
      }

      days.push({
        date: d,
        inactive: isOutOfRange,
        isToday,
        isPast,
        isLocked,
      });
    }
    this.calendarDays = days;
  }

  selectDay(day: any) {
    if (day.inactive) return;
    if (!this.activeCalendarRow && !this.showWizardCalendar) return;

    if (day.isLocked && this.calendarMode === 'row-date') {
      this.ux.showError('This date is already selected in your timesheet.');
      return;
    }

    const selectedDate = new Date(
      this.currentYear,
      this.currentMonth,
      day.date,
    );
    const dateStr = this.formatDisplayDate(selectedDate);
    const isoDate = this.toLocalISOString(selectedDate);

    if (this.calendarMode === 'row-date' && this.activeCalendarRow) {
      this.activeCalendarRow.date = dateStr;
      this.calculateHours(this.activeCalendarRow);
    } else if (this.calendarMode === 'off-day') {
      this.tempOffDate = isoDate;
    } else if (this.calendarMode === 'batch-start') {
      this.batchStartDate = isoDate;
    } else if (this.calendarMode === 'batch-end') {
      this.batchEndDate = isoDate;
    }

    this.closeCalendar();
  }

  prevMonth() {
    this.currentMonth--;
    if (this.currentMonth < 0) {
      this.currentMonth = 11;
      this.currentYear--;
    }
    this.generateCalendar();
  }

  nextMonth() {
    this.currentMonth++;
    if (this.currentMonth > 11) {
      this.currentMonth = 0;
      this.currentYear++;
    }
    this.generateCalendar();
  }

  isDateLocked(date: Date): boolean {
    const dateStr = this.formatDisplayDate(date);
    const myRole = this.getCurrentRole();

    // Check if I already have a row for this date
    // EXCEPTION: If we are editing an existing row, that row's own date is NOT locked.
    const myRow = this.rows.find(
      (r) => 
        r.date === dateStr && 
        (r.addedBy || 'Vendor') === myRole && 
        r !== this.activeCalendarRow
    );

    if (myRow) return true;

    return false;
  }

  hasDateConflict(): string | null {
    // Validation: Check for self-duplicates only.
    // With separate tables, "Conflict" with other party is allowed.
    // So we only check if *I* have multiple rows with same date.

    const myRole = this.getCurrentRole();
    const myRows = this.rows.filter((r) => (r.addedBy || 'Vendor') === myRole);

    const dateCounts: Record<string, number> = {};
    for (const row of myRows) {
      dateCounts[row.date] = (dateCounts[row.date] || 0) + 1;
      if (dateCounts[row.date] > 1) return row.date;
    }

    return null;
  }
}
